import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export function assertValidFirestoreDocumentId(branchDocumentId) {
  const value = String(branchDocumentId || '').trim();
  if (!value) throw new Error('branchDocumentId is required');
  if (value.includes('/') || value.includes('\\')) {
    throw new Error(`Invalid branchDocumentId: ${value}`);
  }
  if (value.split('/').length !== 1) {
    throw new Error(`Invalid branchDocumentId path: ${value}`);
  }
  return value;
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

export function normalizeBranchScope({
  branchDocumentId,
  branchCanonicalId = '',
  branchName = '',
  branchAliases = [],
} = {}) {
  const documentId = assertValidFirestoreDocumentId(branchDocumentId);
  return {
    branchDocumentId: documentId,
    branchCanonicalId: String(branchCanonicalId || '').trim(),
    branchName: String(branchName || '').trim(),
    branchAliases: Array.isArray(branchAliases)
      ? branchAliases.map((alias) => String(alias || '').trim()).filter(Boolean)
      : [],
  };
}

export function branchDocumentPath(branchDocumentId, ...segments) {
  return ['vestigingen', assertValidFirestoreDocumentId(branchDocumentId), ...segments];
}

export function branchCollectionPath(branchDocumentId, collectionName) {
  return branchDocumentPath(branchDocumentId, collectionName);
}

function branchValues(value) {
  return [
    value.id,
    value.name,
    value.naam,
    value.branchId,
    value.branchCanonicalId,
    ...(Array.isArray(value.aliases) ? value.aliases : []),
    ...(Array.isArray(value.branchAliases) ? value.branchAliases : []),
  ].map(normalize).filter(Boolean);
}

export function matchBranchScope(input, vestigingen, branches = []) {
  const key = normalize(input);
  if (!key) return null;
  const vestiging = vestigingen.find((value) => branchValues(value).includes(key));
  if (vestiging) {
    return normalizeBranchScope({
      branchDocumentId: vestiging.id,
      branchCanonicalId: vestiging.branchCanonicalId || vestiging.branchId || vestiging.id,
      branchName: vestiging.name || vestiging.naam || vestiging.id,
      branchAliases: vestiging.aliases || vestiging.branchAliases,
    });
  }
  const branch = branches.find((value) => branchValues(value).includes(key));
  if (!branch) return null;
  return matchBranchScope(branch.name || branch.naam || branch.id, vestigingen, []);
}

export async function resolveBranchScopes(inputs) {
  const candidates = Array.from(new Set((inputs || []).map((input) => String(input || '').trim()).filter(Boolean)));
  if (!candidates.length) return { scopes: [], invalid: [] };
  const [branchesSnapshot, vestigingenSnapshot] = await Promise.all([
    getDocs(collection(db, 'branches')),
    getDocs(collection(db, 'vestigingen')),
  ]);
  const branches = branchesSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  const vestigingen = vestigingenSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  const scopes = [];
  const invalid = [];
  candidates.forEach((input) => {
    const scope = matchBranchScope(input, vestigingen, branches);
    if (!scope) {
      invalid.push({ input, reason: 'BRANCH_DOCUMENT_NOT_FOUND' });
      return;
    }
    scopes.push({ input, ...scope });
  });
  return { scopes, invalid };
}
